import { Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { fetchUsage } from "../lib/client";
import { Dices, Menu, X, Sparkles, Music, Radio, GraduationCap, ChevronDown } from "lucide-react";
import { useI18n, LANGUAGES } from "../lib/i18n";

// In the bundled native app (Capacitor) only the caller is shipped; full-site
// pages like /sinterklaas-bingo/ are not bundled, so navigating to them in-app
// white-screens. On native we open those links on the live site in the system browser.
const IS_NATIVE = typeof window !== "undefined" && !!(window.Capacitor && (window.Capacitor.isNativePlatform?.() || window.Capacitor.isNative));
const SITE_ORIGIN = "https://bingocards-generator.com";
const navHref = (href) => (IS_NATIVE && typeof href === "string" && href.startsWith("/")) ? SITE_ORIGIN + href : href;

/**
 * Site header — minimal, refined, mobile-first.
 *
 * Desktop: logo · Music ▾ · AI · Ballen trekker · Voor Leraren (NL) · Blog · lang · CTA
 * Mobile : logo · usage badge · hamburger → drawer with same nav items
 *
 * The "Muziek ▾" dropdown surfaces the sub-genre landing pages (foute-kerst,
 * foute-hits-bedrijfsfeest, foute-hits-bruiloft) that would otherwise only be
 * discoverable via the muziek-bingo hub. Added 2026-08-16 per user feedback:
 * "hoe kom ik als leraar op de niche pagina? menu met snellinks?".
 */
export default function Header({ usageVersion = 0 }) {
  const [usage, setUsage] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);
  const musicRef = useRef(null);
  const location = useLocation();
  const { t, lang, setLang } = useI18n();

  useEffect(() => {
    let active = true;
    fetchUsage().then((u) => active && setUsage(u)).catch(() => {});
    return () => { active = false; };
  }, [usageVersion, location.pathname]);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close music dropdown on outside click
  useEffect(() => {
    function onDown(e) {
      if (musicOpen && musicRef.current && !musicRef.current.contains(e.target)) {
        setMusicOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [musicOpen]);

  // Body scroll lock + ESC handler
  useEffect(() => {
    function onEsc(e) { if (e.key === "Escape") setMobileOpen(false); }
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onEsc);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [mobileOpen]);

  const remaining = usage ? usage.remaining : "…";
  const nearLimit = usage && usage.remaining <= 5 && !usage.unlocked;
  const isPro = usage?.unlocked;

  // AI landing URL per language (Dutch & English use same slug, German differs)
  const aiHref = lang === "de" ? "/de/ki-bingo-generator/" : `/${lang}/ai-bingo-generator/`;

  // Number-caller landing URL per language
  const numberGenHref =
    lang === "en" ? "/en/bingo-number-generator/" :
    lang === "de" ? "/de/bingo-zahlen-generator/" :
    "/nl/bingo-getallen-generator/";

  // Music-bingo hub URL per language
  const musicHubHref =
    { nl: "/nl/muziek-bingo/", en: "/en/music-bingo/", de: "/de/musik-bingo/" }[lang] || "/nl/muziek-bingo/";

  // Music sub-genre quick-links (kept for the mobile drawer sub-nav only)
  const musicSubLinks = lang === "nl"
    ? [
        { label: t("music_sub_foute_kerst"), href: "/nl/muziek-bingo/foute-kerst-hits/", emoji: "🎄" },
        { label: t("music_sub_foute_bedrijf"), href: "/nl/muziek-bingo/foute-hits-bedrijfsfeest/", emoji: "🥂" },
        { label: t("music_sub_foute_bruiloft"), href: "/nl/muziek-bingo/foute-hits-bruiloft/", emoji: "💍" },
        { label: t("music_sub_nederlandstalig"), href: "/nl/muziek-bingo/nederlandstalig/", emoji: "🇳🇱" },
      ]
    : lang === "de"
      ? [
          { label: "80er Hits", href: "/de/musik-bingo/80er/", emoji: "🎸" },
          { label: "90er Hits", href: "/de/musik-bingo/90er/", emoji: "🎶" },
          { label: "Guilty Pleasures", href: "/de/musik-bingo/guilty-pleasures/", emoji: "🎉" },
        ]
      : [
          { label: "80s Hits", href: "/en/music-bingo/80s/", emoji: "🎸" },
          { label: "90s Hits", href: "/en/music-bingo/90s/", emoji: "🎶" },
          { label: "Guilty Pleasures", href: "/en/music-bingo/guilty-pleasures/", emoji: "🎉" },
        ];

  // ─── SIX CORE BINGO PATHS ────────────────────────────────────────────────
  // The dropdown and Landing "choose your bingo" grid share the same 6 items
  // (single source of truth). Order = user preference:
  //   1. Music+Spotify (highest revenue) 2. Music only 3. Number 4. Theme
  //   5. AI 6. Custom upload.
  const bingoPaths = [
    { key: "sinterklaas",   emoji: "🎁", href: "/sinterklaas-bingo/",      color: "#C8102E" },
    { key: "music_spotify", emoji: "🎧", href: `/create?theme=muziek`, color: "#1DB954" },
    { key: "music_only",    emoji: "🎵", href: musicHubHref,               color: "#7B1FA2" },
    { key: "number_bingo",  emoji: "🎲", href: "/create?numbers=75",       color: "#1976D2" },
    { key: "theme_bingo",   emoji: "🎉", href: "/create",                  color: "#E53935" },
    { key: "ai_bingo",      emoji: "✨", href: aiHref,                     color: "#F57C00" },
    { key: "custom_bingo",  emoji: "✏️", href: "/create?blank=1",          color: "#5D4037" },
    { key: "ball_drawer",   emoji: "🎱", href: "/caller",                 color: "#0097A7" },
  ];

  return (
    <header className="w-full border-b-2 border-black bg-white" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 py-2.5 sm:py-4 flex items-center justify-between gap-2">
        {/* Logo — compact on mobile ("b-g"), full brand on ≥sm */}
        <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="logo-link">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-md nb-border bg-[var(--brand-yellow)] flex items-center justify-center nb-shadow-sm">
            <Dices size={20} strokeWidth={2.5} />
          </div>
          <div className="font-display font-black tracking-tight" style={{ letterSpacing: "-0.03em" }}>
            {/* Mobile: short "b-g" mark */}
            <span className="text-xl sm:hidden">b<span className="text-[var(--brand-red)]">-</span>g</span>
            {/* Tablet+: full wordmark */}
            <span className="hidden sm:inline sm:text-lg lg:text-xl">bingocards<span className="text-[var(--brand-red)]">-</span>generator</span>
          </div>
        </Link>

        {/* Desktop nav — one clean dropdown with the 6 core bingo paths.
            Simplified 2026-02-28 per user feedback: "wat? kiezen! doorverwijzen". */}
        <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
          <div className="relative" ref={musicRef} data-testid="nav-main-dropdown-wrap">
            <button
              onClick={() => setMusicOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-black hover:text-[var(--brand-red)] px-3 py-2 rounded-md transition-colors"
              aria-expanded={musicOpen}
              aria-haspopup="true"
              data-testid="nav-main-dropdown-btn"
            >
              <Menu size={15} />
              {t("nav_which_today")}
              <ChevronDown size={13} className={`transition-transform ${musicOpen ? "rotate-180" : ""}`} />
            </button>
            {musicOpen && (
              <div
                className="absolute left-0 top-full mt-1 min-w-[340px] nb-border rounded-md bg-white nb-shadow-lg z-50 py-2"
                data-testid="nav-main-dropdown-menu"
                role="menu"
              >
                {bingoPaths.map((p) => (
                  <a
                    key={p.key}
                    href={navHref(p.href)}
                    target={IS_NATIVE ? "_blank" : undefined}
                    rel={IS_NATIVE ? "noopener noreferrer" : undefined}
                    className="flex items-start gap-3 px-4 py-2.5 hover:bg-[var(--brand-yellow)] transition-colors"
                    data-testid={`menu-${p.key}`}
                    role="menuitem"
                  >
                    <span className="text-xl leading-none pt-0.5" style={{ color: p.color }}>{p.emoji}</span>
                    <div>
                      <div className="text-sm font-black text-black leading-tight">{t(`menu_${p.key}`)}</div>
                      <div className="text-[11px] text-neutral-600 font-medium mt-0.5">{t(`menu_${p.key}_sub`)}</div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Right-side: lang + usage + CTA + mobile hamburger */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Language switcher (compact pill, always visible) */}
          <div className="inline-flex nb-border rounded-full overflow-hidden bg-white" data-testid="language-switcher">
            {LANGUAGES.map((L) => (
              <button
                key={L.id}
                onClick={() => setLang(L.id)}
                className={`px-2 py-1 text-[11px] font-bold ${lang === L.id ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"}`}
                data-testid={`lang-${L.id}`}
                aria-label={L.label}
              >
                {L.id.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Usage indicator (desktop only) */}
          {isPro ? (
            <div className="hidden lg:flex items-center gap-1 bg-[var(--brand-green)] text-white px-2.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider" data-testid="usage-badge">
              PRO · {t("unlocked")}
            </div>
          ) : (
            <div
              className={`hidden lg:flex items-center gap-1.5 border border-neutral-300 bg-white px-2.5 py-1.5 rounded-full text-[11px] font-semibold ${nearLimit ? "pulse-warn bg-[var(--brand-yellow)] border-black" : "text-neutral-700"}`}
              data-testid="usage-badge"
            >
              <span className="font-mono">{remaining}</span>
              <span className="opacity-70">{t("free_remaining")}</span>
            </div>
          )}

          {/* Primary CTA — only colored button in the header */}
          <Link
            to="/create"
            className="nb-button bg-[var(--brand-red)] text-white px-3 sm:px-5 py-2 rounded-md text-sm whitespace-nowrap"
            data-testid="header-create-btn"
          >
            {t("nav_create")}
          </Link>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden nb-border bg-white p-2 rounded-md"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu size={18} />
          </button>
        </div>
      </div>

      {/* Mobile row 2 — full-width "Which bingo today?" pill.
          Only shown on <md (mobile/tablet). Opens the same drawer as the
          hamburger for a consistent one-menu experience. Added 2026-02-28
          per user feedback: "menu valt uit beeld, maak 2 regels". */}
      <div className="md:hidden border-t border-black/10 bg-[var(--brand-yellow)]">
        <button
          onClick={() => setMobileOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 font-display font-black text-sm text-black"
          data-testid="mobile-menu-row-btn"
        >
          <Menu size={15} />
          {t("nav_which_today")}
          <ChevronDown size={13} />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[100]" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-[86vw] max-w-[340px] bg-white border-l-2 border-black overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b-2 border-black">
              <div className="font-display font-black text-lg">Menu</div>
              <button onClick={() => setMobileOpen(false)} className="nb-border bg-white p-1.5 rounded-md" aria-label="Close menu" data-testid="mobile-menu-close">
                <X size={18} />
              </button>
            </div>

            <nav className="p-3 space-y-1">
              <Link to="/" className="flex items-center gap-2 px-3 py-3 rounded-md font-bold text-sm hover:bg-neutral-100">
                <span className="text-base">🏠</span> {t("nav_home") || "Home"}
              </Link>

              {/* 6 core bingo paths — same order as desktop dropdown */}
              <div className="px-3 pt-3 pb-1 text-[10px] font-black uppercase tracking-widest text-neutral-500">
                {t("nav_which_today")}
              </div>
              {bingoPaths.map((p) => (
                <a
                  key={p.key}
                  href={navHref(p.href)}
                  target={IS_NATIVE ? "_blank" : undefined}
                  rel={IS_NATIVE ? "noopener noreferrer" : undefined}
                  className="flex items-start gap-3 px-3 py-2.5 rounded-md hover:bg-[var(--brand-yellow)]"
                  data-testid={`mobile-menu-${p.key}`}
                >
                  <span className="text-xl leading-none pt-0.5" style={{ color: p.color }}>{p.emoji}</span>
                  <div>
                    <div className="text-sm font-black text-black leading-tight">{t(`menu_${p.key}`)}</div>
                    <div className="text-[11px] text-neutral-600 mt-0.5">{t(`menu_${p.key}_sub`)}</div>
                  </div>
                </a>
              ))}

              {/* Extras — small, tucked at the bottom */}
              <div className="pt-3 border-t border-neutral-200 mt-3">
                {lang === "nl" && (
                  <a href="/nl/bingo-voor-leraren/" className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-100" data-testid="mobile-nav-teachers">
                    <GraduationCap size={15} /> {t("nav_teachers")}
                  </a>
                )}
                {lang === "nl" && (
                  <a href="/beste-bingo-generator-2026/" className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-100" data-testid="mobile-nav-compare">
                    📊 {t("nav_compare")}
                  </a>
                )}
              </div>
            </nav>

            {/* Mobile usage indicator */}
            <div className="p-4 border-t border-neutral-200 mt-2">
              {isPro ? (
                <div className="bg-[var(--brand-green)] text-white px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wider text-center">
                  PRO · {t("unlocked")}
                </div>
              ) : (
                <div className="text-center text-xs text-neutral-700">
                  <span className="font-mono font-bold text-base">{remaining}</span>{" "}
                  {t("free_remaining")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
